import {tva} from "@gluestack-ui/nativewind-utils/tva";
import buildChannels from "@hosttools/core/shared/buildChannels";
import {memo, useContext, useMemo} from "react";
import type {FC} from "react";

import type {Listing, SelectChannelType} from "../../models/listing";
import type {PriceChannelCustom} from "../../models/price";
import {UserContext} from "../../react";
import {generateDateTextByKey, getPriceChannels} from "../../utils/calendar";
import {HStack} from "../ui/hstack";
import {VStack} from "../ui/vstack";

import Item from "./Item";

interface Props {
    listing: Listing;
    channel: SelectChannelType;
    price?: PriceChannelCustom;
    isPast: boolean;
    mode?: "single" | "multiple";
}

const wrapperStyle = tva({
    base: "native:absolute native:z-0 native:p-1 flex h-full w-full self-start justify-between",
    variants: {
        mode: {
            single: "justify-end gap-2 native:p-2",
            multiple: ""
        }
    }
});

const CalendarDateInfo: FC<Props> = ({listing, channel, price, isPast, mode}) => {
    const {permissions} = useContext(UserContext);

    const hasPermissionViewPricing = permissions.pricing?.view;
    const hasPermissionViewAvailability = permissions.availability?.view;
    const listingChannels = useMemo(() => buildChannels(listing), [listing]);
    const {pricingEnabled} = listing;
    const {blocked, priceOverride} = price ?? {};
    const wasOverridePrices = useMemo(() => {
        return (
            !!priceOverride || !!listingChannels.find(channel => price?.[channel]?.priceOverride)
        );
    }, [priceOverride, listingChannels, price]);

    const {minNights, priceText, blockedCheckin, blockedCheckout} = useMemo(() => {
        let priceText = "";
        let minNights = "";
        let blockedCheckin: boolean | undefined;
        let blockedCheckout: boolean | undefined;

        if (pricingEnabled && price) {
            if (channel === "all") {
                const priceChannels = getPriceChannels(price, listingChannels);
                if (priceChannels.length > 0) {
                    priceText = generateDateTextByKey(
                        priceChannels.filter(Boolean),
                        "price",
                        "priceFormatted"
                    );
                    minNights = generateDateTextByKey(
                        priceChannels.filter(Boolean),
                        "minNights",
                        "minNights"
                    );
                    blockedCheckin = priceChannels.some(price => price?.blockCheckin);
                    blockedCheckout = priceChannels.some(price => price?.blockCheckout);
                } else {
                    priceText = "";
                }
            } else {
                const priceChannel = price[channel];
                const hasChannel = listing.channels[channel];

                if (hasChannel && priceChannel) {
                    minNights = priceChannel.minNights ? `${priceChannel.minNights}` : "";
                    const {priceFormatted, price} = priceChannel;
                    if (priceFormatted && price) {
                        priceText = priceFormatted;
                    }
                    blockedCheckin = priceChannel.blockCheckin;
                    blockedCheckout = priceChannel.blockCheckout;
                } else {
                    priceText = "";
                }
            }
        }

        return {
            minNights,
            priceText,
            listingChannels,
            blockedCheckin,
            blockedCheckout
        };
    }, [listing, channel, price, pricingEnabled, listingChannels]);

    return !blocked && !isPast && hasPermissionViewAvailability ? (
        <VStack className={wrapperStyle({mode})}>
            <VStack className="date-badge-container" space="2xs">
                {minNights && (
                    <Item kind="minNights" testID={`minNights-${minNights}`} text={minNights} />
                )}
                {(blockedCheckin || blockedCheckout) && (
                    <HStack className="blocked-date-group" space="2xs">
                        {blockedCheckin && <Item kind="blockedCheckin" />}
                        {blockedCheckout && <Item kind="blockedCheckout" />}
                    </HStack>
                )}
            </VStack>
            {hasPermissionViewPricing && priceText && (
                <Item
                    isBoldText={wasOverridePrices}
                    testID={`price-${channel}-${priceText}`}
                    text={priceText}
                />
            )}
        </VStack>
    ) : null;
};

export default memo(CalendarDateInfo);
